import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from 'utils/hooks/useQuery';
import usePayment from 'state/payment/hooks/usePayment';
import { TIPS_TABS } from 'utils/constants';

interface UseTipDetailsArgs {
  activeTab: string;
  dateFrom: string;
  dateTo: string;
  firstAmount: string | undefined;
  secondAmount: string | undefined;
  amountOption: string;
  paymentTypeArray: string[];
  storeIdArray: string[];
  searchString: string | undefined;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
}

interface UseTipDetailsReturn {
  prevKey: string | null;
  nextKey: string | null;
  numResults: number;
  prevPage: () => void;
  nextPage: () => void;
}

const useTipDetails = ({
  activeTab,
  dateFrom,
  dateTo,
  firstAmount,
  secondAmount,
  amountOption,
  paymentTypeArray,
  storeIdArray,
  searchString,
  setActiveTab,
}: UseTipDetailsArgs): UseTipDetailsReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery(location.search);
  const { payment, toFetchTipsList } = usePayment();

  const [prevKey, setPrevKey] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [numResults, setNumResults] = useState(0);

  const buildFetchParams = useCallback(
    (key: string | null = null) => ({
      key,
      start_date: dateFrom || undefined,
      end_date: dateTo || undefined,
      amount_1: firstAmount,
      amount_2: secondAmount,
      condition: amountOption || undefined,
      payment_type:
        paymentTypeArray.length > 0 ? paymentTypeArray.join(',') : undefined,
      selected_entity_ids:
        storeIdArray.length > 0 ? storeIdArray.join(',') : undefined,
      search_string: searchString,
    }),
    [
      dateFrom,
      dateTo,
      firstAmount,
      secondAmount,
      amountOption,
      paymentTypeArray,
      storeIdArray,
      searchString,
    ]
  );

  const prevPage = useCallback(() => {
    toFetchTipsList(buildFetchParams(prevKey));
  }, [buildFetchParams, prevKey, toFetchTipsList]);

  const nextPage = useCallback(() => {
    toFetchTipsList(buildFetchParams(nextKey));
  }, [buildFetchParams, nextKey, toFetchTipsList]);

  useEffect(() => {
    if (payment.tips?.items) {
      setNextKey(payment.tips.next_key);
      setPrevKey(payment.tips.prev_key);
      setNumResults(payment.tips.total_count);
    }
  }, [payment.tips]);

  useEffect(() => {
    const queryTabParam = query.get('tab')?.toLowerCase();

    if (location.search && queryTabParam) {
      setActiveTab(
        TIPS_TABS.find((t) => t.label.toLowerCase() === queryTabParam)?.label ||
          ''
      );
    } else {
      navigate({ search: '?tab=summary' });
    }

    if (activeTab === TIPS_TABS[1].label) {
      toFetchTipsList(buildFetchParams());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, activeTab]);

  return { prevKey, nextKey, numResults, prevPage, nextPage };
};

export default useTipDetails;
